import { memo, useMemo, useId, useLayoutEffect, useRef, useState } from 'react';
import { contourTerrain } from '@/lib/contourTerrain';
import { TREE_DESIGNS, DISCOVERIES } from '@/lib/sceneryCatalog';

/** A quiet line illustration, rebuilt only as the visible section changes. */
export default memo(function MountainTerrain({day, start}: {day: string; start: number}) {
  const treePaint = useId().replace(/:/g,'');
  const group = useRef<SVGGElement>(null);
  const [edge, setEdge] = useState(125);
  const terrain = useMemo(() => contourTerrain(day,start,edge), [day,start,edge]);
  useLayoutEffect(() => {
    const world=group.current?.closest('.cf-world');
    if (!world) return;
    const measure=()=>setEdge(world.getBoundingClientRect().width);
    measure();
    const observer=new ResizeObserver(measure);
    observer.observe(world);
    return ()=>observer.disconnect();
  }, []);
  // Reject the whole silhouette, not just its anchor, where it touches water.
  const clearOfWater=(x:number,y:number,halfWidth:number,height:number,relativeToEdge=false)=>
    !terrain.rivers.some(river=>river.samples.some(p=>{
      const riverX=p.x+(relativeToEdge?0:edge);
      return riverX>=x-halfWidth-12 && riverX<=x+halfWidth+12 && p.y>=y-height-12 && p.y<=y+8+12;
    }));
  return <g ref={group} className="cf-contour-study">
    <defs>
      <radialGradient id={`${treePaint}-bush`} cx="25%" cy="20%" r="85%">
        <stop offset="0" stopColor="#b8c2a5"/><stop offset=".5" stopColor="#879d7f"/><stop offset="1" stopColor="#657e68"/>
      </radialGradient>
      {TREE_DESIGNS.map(({colors},index)=><radialGradient key={index} id={`${treePaint}-${index}`} cx="27%" cy="30%" r="80%">
        <stop offset="0" stopColor={colors[0]}/><stop offset=".48" stopColor={colors[1]}/><stop offset="1" stopColor={colors[2]}/>
      </radialGradient>)}
      {DISCOVERIES.map(({colors},index)=><radialGradient key={index} id={`${treePaint}-discovery-${index}`} cx="25%" cy="25%" r="85%">
        <stop offset="0" stopColor={colors[0]}/><stop offset=".5" stopColor={colors[1]}/><stop offset="1" stopColor={colors[2]}/>
      </radialGradient>)}
    </defs>
    <svg x="100%" width="1" height="1" overflow="visible">
      {terrain.rivers.map(river=><g key={river.id} className="cf-map-river"><path className="cf-map-river-bank" d={river.d}/><path className="cf-map-river-water" d={river.d}/></g>)}
      {terrain.bushes.filter(bush=>clearOfWater(bush.x,bush.y,22*bush.scale,25*bush.scale,true)).map(bush=><g key={bush.id} className="cf-map-bush" transform={`translate(${bush.x} ${bush.y}) scale(${bush.scale})`}>
        <title>Small bush</title><ellipse className="cf-map-tree-shadow" cx="3" cy="2" rx="11" ry="3"/>
        <path className="cf-map-tree-trunk" d="M 0 2 L -1 -5 M -1 -2 L 5 -6"/>
        <path className="cf-map-tree-canopy" style={{fill:`url(#${treePaint}-bush)`}} d={[
          'M -12 -2 C -18 -8 -10 -15 -6 -11 C -7 -21 6 -21 8 -12 C 17 -16 21 -4 12 -1 Q 1 4 -12 -2 Z',
          'M -14 -1 C -20 -6 -14 -14 -8 -12 C -5 -21 5 -17 6 -10 C 14 -15 22 -5 13 0 Q 0 4 -14 -1 Z',
          'M -10 0 C -17 -4 -13 -12 -7 -12 C -11 -20 2 -24 5 -16 C 15 -19 19 -8 12 -5 C 18 2 1 4 -10 0 Z',
        ][bush.variant]}/>
        <path className="cf-map-tree-detail" d="M -8 -7 Q -6 -12 -2 -10 M 2 -14 Q 6 -15 8 -11"/>
      </g>)}
    </svg>
    <g className="cf-map-washes">{terrain.washes.map(wash=><path key={wash.id} className="cf-map-wash" d={wash.d} style={{fill:wash.color,fillOpacity:wash.opacity}}/>)}</g>
    <path className="cf-map-ground" d={terrain.base}/>
    {terrain.contours.map(line=><path key={line.id} className={line.outer ? 'cf-map-outline' : 'cf-map-contour'} d={line.d}/>)}
    {terrain.hatches.map(line=><path key={line.id} className="cf-map-slope" d={line.d}/>)}
    {terrain.trees.filter(tree=>clearOfWater(tree.x,tree.y,25*tree.scale,65*tree.scale)).map(tree=><g key={tree.id} className={`cf-map-tree cf-map-tree-${tree.variant}`} transform={`translate(${tree.x} ${tree.y}) scale(${tree.scale})`}>
      <ellipse className="cf-map-tree-shadow" cx="4" cy="2" rx="9" ry="2.8" transform="rotate(15 4 2)"/>
      <title>{TREE_DESIGNS[tree.variant].name}</title>
      <path className="cf-map-tree-trunk" d={TREE_DESIGNS[tree.variant].trunk}/>
      <path className="cf-map-tree-canopy" style={{fill:`url(#${treePaint}-${tree.variant})`}} d={TREE_DESIGNS[tree.variant].crown}/>
      <path className="cf-map-tree-detail" d={TREE_DESIGNS[tree.variant].detail}/>

    </g>)}
    {terrain.curios.filter(item=>clearOfWater(item.x,item.y,40,75)).map(item=><g key={item.id} className="cf-map-curio" transform={`translate(${item.x} ${item.y})`}>
      <title>{DISCOVERIES[item.kind].name}</title>
      <ellipse className="cf-map-discovery-shadow" cx="3" cy="3" rx="12" ry="2.5"/>
      <path style={{fill:`url(#${treePaint}-discovery-${item.kind})`,stroke:DISCOVERIES[item.kind].colors[2]}} d={DISCOVERIES[item.kind].d}/>
      {DISCOVERIES[item.kind].name === 'Mountain cabin' && <>
        <path className="cf-discovery-accent" style={{fill:'#a38e79'}} d="M -16 -15 L 0 -28 L 17 -15 L 13 -15 L 0 -24 L -12 -15 Z"/>
        <path className="cf-discovery-accent" style={{fill:'#e7d39e'}} d="M -10 -12 H -6 V -7 H -10 Z M 7 -12 H 11 V -7 H 7 Z"/>
      </>}
      {DISCOVERIES[item.kind].name === 'Small tent' && <path className="cf-discovery-accent" style={{fill:'#a99461'}} d="M -12 2 L -3 -14 L 1 2 Z"/>}
      {DISCOVERIES[item.kind].name === 'Fox' && <path className="cf-discovery-accent" style={{fill:'#eee3cf'}} d="M -23 -7 Q -21 -13 -18 -12 L -17 -5 Z"/>}

    </g>)}
  </g>;
});
